import { useEffect, useState } from "react";
import AdminPage from "./components/AdminPage";

import TaskForm from "./components/tasks/TaskForm";
import TaskFilters from "./components/tasks/TaskFilters";
import TaskCard from "./components/tasks/TaskCard";

import {
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  uploadAttachment,
  deleteAttachment,
  addComment,
  deleteComment,
} from "./api/tasksApi";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "low",
    assigned_to: "",
  });

  const [filters, setFilters] = useState({
    search: "",
    assigned_to: "all",
    status: "all",
    priority: "all",
    date: "all",
  });

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editingAssignedId, setEditingAssignedId] = useState(null);
  const [assignedValue, setAssignedValue] = useState("");
  const [commentInputs, setCommentInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadTasks();
  }, []);

  async function loadTasks() {
    setLoading(true);
    try {
      const data = await getTasks();
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();

    if (!form.title.trim()) return;

    setLoading(true);

    try {
      const task = await createTask(form);

      setTasks((prev) => [task, ...prev]);

      setForm({
        title: "",
        description: "",
        priority: "low",
        assigned_to: "",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdate(id, updates) {
    const updated = await updateTask(id, updates);

    setTasks((prev) =>
      prev.map((task) => (task.id === id ? { ...task, ...updated } : task)),
    );
  }

  async function handleDelete(id) {
    await deleteTask(id);

    setTasks((prev) => prev.filter((task) => task.id !== id));
  }

  async function handleDeleteAttachment(taskId, id) {
    await deleteAttachment(id);

    setTasks((prev) =>
      prev.map((task) => {
        if (task.id !== taskId) return task;

        return {
          ...task,

          attachments: task.attachments.filter((a) => a.id !== id),
        };
      }),
    );
  }

  async function handleAddComment(taskId) {
    const comment = commentInputs[taskId]?.trim();

    if (!comment) return;

    setSubmittingComment(true);

    try {
      const result = await addComment(taskId, comment);

      setTasks((prev) =>
        prev.map((task) => {
          if (task.id !== taskId) return task;

          return {
            ...task,

            comments: [...(task.comments || []), result],
          };
        }),
      );

      setCommentInputs((prev) => ({ ...prev, [taskId]: "" }));
    } finally {
      setSubmittingComment(false);
    }
  }

  const filteredTasks = tasks.filter((task) => {
    if (
      filters.search &&
      !task.title.toLowerCase().includes(filters.search.toLowerCase())
    )
      return false;

    if (filters.status !== "all" && task.status !== filters.status)
      return false;

    if (filters.priority !== "all" && task.priority !== filters.priority)
      return false;

    if (
      filters.assigned_to !== "all" &&
      task.assigned_to !== filters.assigned_to
    )
      return false;

    return true;
  });

  return (
    <AdminPage title='Tasks' subtitle='Manage internal tasks'>
      <div className='max-w-5xl mx-auto'>
        <div className='flex justify-between mb-8'>
          <h1 className='text-4xl font-bold'>Tasks</h1>
          <div className='text-slate-400'>{tasks.length} tasks</div>
        </div>

        <TaskForm
          form={form}
          setForm={setForm}
          onSubmit={handleCreate}
          loading={loading}
        />

        <TaskFilters filters={filters} setFilters={setFilters} tasks={tasks} />

        <div className='space-y-5'>
          {filteredTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onImageClick={setSelectedImage}
              onDeleteAttachment={handleDeleteAttachment}
              onAddComment={handleAddComment}
              onDeleteComment={deleteComment}
              editingTaskId={editingTaskId}
              editDescription={editDescription}
              setEditDescription={setEditDescription}
              saveDescription={async (id) => {
                const updated = await updateTask(id, {
                  description: editDescription,
                });

                setTasks((prev) =>
                  prev.map((t) =>
                    t.id === id
                      ? {
                          ...t,
                          ...updated,
                        }
                      : t,
                  ),
                );

                setEditingTaskId(null);
              }}
              setEditingTaskId={setEditingTaskId}
              editingAssignedId={editingAssignedId}
              assignedValue={assignedValue}
              setAssignedValue={setAssignedValue}
              startEditAssigned={(task) => {
                setEditingAssignedId(task.id);

                setAssignedValue(task.assigned_to || "");
              }}
              saveAssignedTo={async (id) => {
                await handleUpdate(id, {
                  assigned_to: assignedValue,
                });

                setEditingAssignedId(null);
              }}
              commentInputs={commentInputs}
              setCommentInputs={setCommentInputs}
              submittingComment={submittingComment}
            />
          ))}
        </div>
      </div>
    </AdminPage>
  );
}
